import { getMyAllReports } from '@/api/workforce';
import { Badge } from '@/components/ui/Badge';
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete';
import { useAuth } from '@/hooks/useAuth';
import { monumentaChartColors, monumentaChartPresets, monumentaChartTheme } from '@/lib/components/charts/monumenta-chart-theme';
import type { Evaluation } from '@/types/performance';
import type { Employee as WorkforceEmployee } from '@/types/workforce';
import { Link } from '@tanstack/react-router';
import { Activity, AlertCircle, BarChart3, BookOpen, Calendar, ChevronDown, ChevronUp, FileText, Filter, Loader2, Plus, Trash2, TrendingUp, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';

// 🚀 NEW: TanStack Query hooks for server state
import { useAllEvaluations, useDeleteEvaluation, useEmployeeEvaluations, useMyCreatedEvaluations, useMyEvaluations, usePerformanceByTheme, useSubmitEvaluation } from '@/hooks/queries';

// 🎨 NEW: Zustand UI store for client state
import { useEvaluationUIStore } from '@/stores/evaluation-ui-store';

const AvaliacaoPerformance = () => {
  const { user } = useAuth();

  // 🎨 UI State (Zustand)
  const {
    expandedSections,
    toggleSection,
    statusFilter,
    setStatusFilter,
    showManagerView,
    setShowManagerView,
    selectedEmployeeId,
    setSelectedEmployeeId,
  } = useEvaluationUIStore()

  // Check if user is a manager
  const isManager = user?.role && ['admin', 'ceo', 'vice-presidente', 'diretor', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh'].includes(user.role) || false;

  // Check if user is admin (can see all evaluations + delete any)
  const isAdmin = user?.role && ['admin', 'rh', 'analista-rh'].includes(user.role) || false;

  // Check if user should see autocomplete search (executives + HR with large teams)
  const showAutocomplete = user?.role && ['admin', 'rh', 'analista-rh', 'ceo', 'vice-presidente'].includes(user.role) || false;

  // Admin "show all" toggle state
  const [showAllEvaluations, setShowAllEvaluations] = useState(false);

  // 🚀 Server State (TanStack Query) - My Evaluations
  const {
    data: myEvaluationsData,
    isLoading: myLoading,
    isFetching: myFetching,
    error: myError,
  } = useMyEvaluations({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 25,
  })

  // 🚀 Server State (TanStack Query) - Evaluations I Created (as evaluator)
  const {
    data: myCreatedEvaluationsData,
    isLoading: createdLoading,
  } = useMyCreatedEvaluations({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 25,
  })

  // 🚀 Server State (TanStack Query) - Employee Evaluations (Manager View)
  const {
    data: employeeEvaluationsData,
    isLoading: employeeLoading,
    isFetching: employeeFetching,
    error: employeeError,
  } = useEmployeeEvaluations(selectedEmployeeId, {
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 25,
  })

  // 🚀 Server State (TanStack Query) - All Evaluations (Admin View)
  const {
    data: allEvaluationsData,
    isLoading: allLoading,
    isFetching: allFetching,
    error: allError,
  } = useAllEvaluations({
    status: statusFilter === 'all' ? undefined : statusFilter,
    limit: 50,
  }, isAdmin && showAllEvaluations)

  // 🔄 Mutations
  const submitEvaluationMutation = useSubmitEvaluation()
  const deleteEvaluationMutation = useDeleteEvaluation()

  // 📊 Analytics - Performance by Theme (from backend API, not local calculation)
  const {
    data: performanceByThemeData,
    isLoading: analyticsLoading,
  } = usePerformanceByTheme()

  // Local state for workforce employees
  const [realWorkforceEmployees, setRealWorkforceEmployees] = useState<WorkforceEmployee[]>([]);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const submitError = null; // Errors handled by mutation hook

  // ✅ Derived state: merge "about me" + "created by me" into one list (deduped), or show employee view
  const mergedMyEvaluations = useMemo(() => {
    const aboutMe = myEvaluationsData?.items || []
    const createdByMe = myCreatedEvaluationsData?.items || []
    const seen = new Set<string>()
    const merged: Evaluation[] = []
    for (const e of [...createdByMe, ...aboutMe]) {
      if (!seen.has(e.evaluationId)) {
        seen.add(e.evaluationId)
        merged.push(e)
      }
    }
    return merged
  }, [myEvaluationsData, myCreatedEvaluationsData])

  const evaluations = showAllEvaluations && isAdmin
    ? allEvaluationsData?.items || []
    : showManagerView && selectedEmployeeId
      ? employeeEvaluationsData?.items || []
      : mergedMyEvaluations

  const loading = showAllEvaluations && isAdmin
    ? allLoading
    : showManagerView && selectedEmployeeId ? employeeLoading : (myLoading || createdLoading)
  const isFetching = showAllEvaluations && isAdmin
    ? allFetching
    : showManagerView && selectedEmployeeId ? employeeFetching : myFetching
  const error = showAllEvaluations && isAdmin
    ? allError
    : showManagerView && selectedEmployeeId ? employeeError : myError
  const pagination = showAllEvaluations && isAdmin
    ? allEvaluationsData?.pagination || { itemCount: 0 }
    : showManagerView && selectedEmployeeId
      ? employeeEvaluationsData?.pagination || { itemCount: 0 }
      : myEvaluationsData?.pagination || { itemCount: 0 }

  // Load workforce employees for manager view
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const workforceResponse = await getMyAllReports({ limit: 100 });
        setRealWorkforceEmployees(workforceResponse.items);
      } catch (workforceErr) {
        console.error('Failed to load workforce employees:', workforceErr);
      }
    };

    loadEmployees();
  }, []);

  // Handle evaluation deletion
  const handleDeleteEvaluation = async (evaluationId: string, employeeName: string, status: string) => {
    const statusLabel = status === 'draft' ? 'Rascunho' : status === 'submitted' ? 'Registrada' : status;
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir a avaliação de "${employeeName}" (${statusLabel})?\n\nEsta ação não pode ser desfeita.`
    );
    if (!confirmed) return;

    try {
      await deleteEvaluationMutation.mutateAsync(evaluationId);
    } catch (err: any) {
      console.error('Error deleting evaluation:', err);
    }
  };

  // Handle evaluation submission
  const handleSubmitEvaluation = async (evaluationId: string) => {
    try {
      await submitEvaluationMutation.mutateAsync(evaluationId);
      setSubmitSuccess('Avaliação registrada com sucesso!');

      setTimeout(() => {
        setSubmitSuccess(null);
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting evaluation:', err);
      // Error handled by mutation hook
    }
  };

  // Calculate analytics from evaluations AND backend API data
  const analyticsData = useMemo(() => {
    if (evaluations.length === 0 && !performanceByThemeData?.length) return null;

    // 📊 Theme Averages: Use REAL per-theme data from backend API
    // This fixes the bug where overallScore was incorrectly distributed to all themes
    const themeAverages = (performanceByThemeData || []).map(item => ({
      theme: item.theme,
      avg: item.averageScore || 0
    }));

    // 🎯 Radar Chart Data: Calculate average score per evaluator type
    const evaluatorScores: Record<string, number[]> = {
      self: [],
      manager: [],
      hr: [],
      peer: []
    };

    evaluations.forEach(evaluation => {
      if (evaluation.overallScore && evaluation.evaluatorType) {
        evaluatorScores[evaluation.evaluatorType]?.push(evaluation.overallScore);
      }
    });

    const avgByEvaluator = {
      autoavaliacao: evaluatorScores.self.length > 0
        ? evaluatorScores.self.reduce((a, b) => a + b, 0) / evaluatorScores.self.length
        : 0,
      gestor: evaluatorScores.manager.length > 0
        ? evaluatorScores.manager.reduce((a, b) => a + b, 0) / evaluatorScores.manager.length
        : 0,
      rh: evaluatorScores.hr.length > 0
        ? evaluatorScores.hr.reduce((a, b) => a + b, 0) / evaluatorScores.hr.length
        : 0
    };

    // Build radar data using real theme data with evaluator overlay
    const radarData = themeAverages.length > 0
      ? themeAverages.map(item => ({
          tema: item.theme,
          autoavaliacao: avgByEvaluator.autoavaliacao || item.avg,
          gestor: avgByEvaluator.gestor || item.avg,
          rh: avgByEvaluator.rh || item.avg
        }))
      : [];

    // 📈 Score Distribution: Based on overall evaluation scores (this is correct)
    const scoreRanges: Record<string, number> = {
      'Excelente (4.5-5.0)': 0,
      'Muito Bom (4.0-4.4)': 0,
      'Bom (3.5-3.9)': 0,
      'Adequado (3.0-3.4)': 0,
      'Precisa Melhorar (<3.0)': 0
    };

    evaluations.forEach(evaluation => {
      if (evaluation.overallScore) {
        const score = evaluation.overallScore;
        if (score >= 4.5) scoreRanges['Excelente (4.5-5.0)']++;
        else if (score >= 4.0) scoreRanges['Muito Bom (4.0-4.4)']++;
        else if (score >= 3.5) scoreRanges['Bom (3.5-3.9)']++;
        else if (score >= 3.0) scoreRanges['Adequado (3.0-3.4)']++;
        else scoreRanges['Precisa Melhorar (<3.0)']++;
      }
    });

    const distributionData = Object.entries(scoreRanges).map(([nivel, quantidade]) => ({
      nivel,
      quantidade,
      color: nivel.includes('Excelente') ? monumentaChartColors.success
        : nivel.includes('Muito Bom') ? monumentaChartColors.info
        : nivel.includes('Bom') ? '#8b5cf6'
        : nivel.includes('Adequado') ? monumentaChartColors.warning
        : monumentaChartColors.error
    }));

    // 💡 Insights: Based on real theme averages from API
    const maxTheme = themeAverages.length > 0
      ? themeAverages.reduce((max, item) => item.avg > max.avg ? item : max, themeAverages[0])
      : { theme: 'N/A', avg: 0 };
    const minTheme = themeAverages.length > 0
      ? themeAverages.reduce((min, item) => item.avg < min.avg ? item : min, themeAverages[0])
      : { theme: 'N/A', avg: 0 };

    return {
      radarData,
      distributionData,
      themeAverages,
      insights: {
        max: maxTheme,
        min: minTheme
      }
    };
  }, [evaluations, performanceByThemeData]);

  // Helper functions for status
  const getStatusBadgeColor = (status: Evaluation['status']) => {
    const colors = {
      draft: 'bg-warning/20 border border-warning/30 text-warning',
      submitted: 'bg-brand-blue/20 border border-brand-blue/30 text-brand-blue',
      reviewed: 'bg-success/20 border border-success/30 text-success',
      completed: 'bg-brand-primary/20 border border-brand-primary/30 text-brand-primary'
    };
    return colors[status] || 'bg-brand-dark-light border border-brand-gray/30 text-brand-gray';
  };

  const getStatusLabel = (status: Evaluation['status']) => {
    const labels = {
      draft: 'Rascunho',
      submitted: 'Registrada',
      reviewed: 'Revisada',
      completed: 'Concluída'
    };
    return labels[status] || status;
  };

  // Calculate summary stats
  const summaryStats = {
    total: evaluations.length,
    draft: evaluations.filter(e => e.status === 'draft').length,
    submitted: evaluations.filter(e => e.status === 'submitted').length,
    completed: evaluations.filter(e => e.status === 'completed').length,
    avgScore: evaluations.length > 0
      ? (evaluations.reduce((acc, e) => acc + (e.overallScore || 0), 0) / evaluations.length).toFixed(1)
      : '0.0'
  };

  const currentEmployeeName = user?.employee?.personalInfo?.name || user?.name || 'Funcionário';

  return (
    <div className="space-y-6">
      {/* Global Success/Error Messages */}
      {submitSuccess && (
        <div className="card bg-brand-dark-light border border-success/30 p-6 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
              <span className="text-white text-xs">✓</span>
            </div>
            <p className="text-success">{submitSuccess}</p>
          </div>
        </div>
      )}

      {submitError && (
        <div className="card bg-brand-dark-light border border-error/30 p-6 animate-fade-in">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-display font-medium text-white mb-1">Erro</h4>
              <p className="text-sm text-brand-gray">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-brand-gray text-sm">Total</span>
            <Activity className="w-5 h-5 text-brand-primary" />
          </div>
          <div className="font-display text-3xl font-bold text-white">{summaryStats.total}</div>
          <p className="text-xs text-brand-gray mt-1">Avaliações</p>
        </div>

        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-brand-gray text-sm">Rascunhos</span>
            <FileText className="w-5 h-5 text-warning" />
          </div>
          <div className="font-display text-3xl font-bold text-white">{summaryStats.draft}</div>
          <p className="text-xs text-brand-gray mt-1">Para completar</p>
        </div>

        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-brand-gray text-sm">Concluídas</span>
            <User className="w-5 h-5 text-success" />
          </div>
          <div className="font-display text-3xl font-bold text-white">{summaryStats.completed}</div>
          <p className="text-xs text-brand-gray mt-1">Finalizadas</p>
        </div>

        <div className="card-hover p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-brand-gray text-sm">Média</span>
            <TrendingUp className="w-5 h-5 text-brand-primary" />
          </div>
          <div className="font-display text-3xl font-bold text-brand-primary">{summaryStats.avgScore}</div>
          <p className="text-xs text-brand-gray mt-1">Pontuação geral</p>
        </div>
      </div>

      {/* NEW: Create New Evaluation CTA - Before List */}
      <Link
        to="/performance/avaliacoes/form"
        className="block group"
      >
        <div className="card bg-gradient-to-br from-brand-primary/10 via-brand-dark-light to-brand-dark-light border-2 border-brand-primary/30 rounded-monumenta overflow-hidden hover:border-brand-primary hover:shadow-lg hover:shadow-brand-primary/20 transition-all duration-300 cursor-pointer">
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-primary rounded-monumenta flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Plus className="w-6 h-6 text-black" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white group-hover:text-brand-primary transition-colors duration-200">
                  Nova Avaliação de Desempenho
                </h2>
                <p className="text-brand-gray text-sm">
                  Inicie uma avaliação completa com assistente guiado
                </p>
              </div>
            </div>
            <div className="text-brand-primary group-hover:translate-x-2 transition-transform duration-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </Link>

      {/* SECTION 1: Evaluations List */}
      <div className="card overflow-hidden">
        <button
          onClick={() => toggleSection('evaluationsList')}
          className="w-full p-6 flex items-center justify-between hover:bg-brand-dark-lighter transition-all duration-200 group"
        >
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-monumenta flex items-center justify-center transition-all duration-200 ${
              expandedSections.evaluationsList ? 'bg-brand-primary' : 'bg-brand-dark'
            }`}>
              <FileText className={`w-5 h-5 ${
                expandedSections.evaluationsList ? 'text-black' : 'text-brand-primary'
              }`} />
            </div>
            <div className="text-left">
              <h2 className="font-display text-2xl font-bold text-white group-hover:text-brand-primary transition-colors duration-200">
                Avaliações Registradas
              </h2>
              <p className="text-sm text-brand-gray">
                {evaluations.length} avaliações encontradas
              </p>
            </div>
          </div>
          {expandedSections.evaluationsList ? (
            <ChevronUp className="w-6 h-6 text-brand-primary" />
          ) : (
            <ChevronDown className="w-6 h-6 text-brand-gray group-hover:text-brand-primary transition-colors duration-200" />
          )}
        </button>

        <div
          className={`transition-all duration-300 ease-in-out ${
            expandedSections.evaluationsList ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="border-t border-brand-gray/10">
            <div className="p-6 space-y-6">
              {/* Filters and Controls */}
              <div className="card p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Status Filter */}
                  <div>
                    <label className="form-label">Filtrar por Status</label>
                    <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Todos os status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Status</SelectItem>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="submitted">Registrada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Manager View Toggle */}
                  {isManager && (
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer group" data-testid="team-view-toggle">
                        <input
                          type="checkbox"
                          checked={showManagerView}
                          onChange={(e) => setShowManagerView(e.target.checked)}
                          className="form-checkbox w-4 h-4 rounded border-brand-gray/10 bg-brand-dark text-brand-primary focus:ring-brand-primary hover:border-brand-primary transition-colors duration-200"
                          data-testid="team-toggle"
                        />
                        <span className="text-white text-sm group-hover:text-brand-primary transition-colors duration-200">
                          Visualizar Avaliações da Equipe
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Admin: Show All Evaluations Toggle */}
                  {isAdmin && (
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={showAllEvaluations}
                          onChange={(e) => {
                            setShowAllEvaluations(e.target.checked);
                            if (e.target.checked) {
                              setShowManagerView(false);
                            }
                          }}
                          className="form-checkbox w-4 h-4 rounded border-brand-gray/10 bg-brand-dark text-brand-primary focus:ring-brand-primary hover:border-brand-primary transition-colors duration-200"
                        />
                        <span className="text-white text-sm group-hover:text-brand-primary transition-colors duration-200">
                          Ver Todas as Avaliações
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Employee Selector (Manager View) - Autocomplete for executives, dropdown for others */}
                  {showManagerView && isManager && !showAllEvaluations && (
                    <div data-testid="employee-select-container">
                      <label className="block text-sm font-medium text-brand-gray mb-2">Selecionar Funcionário</label>
                      {showAutocomplete ? (
                        <div className="w-80">
                          <EmployeeAutocomplete
                            onSelect={(employeeId) => setSelectedEmployeeId(employeeId)}
                            navigateOnSelect={false}
                            placeholder="Buscar funcionário..."
                            data-testid="employee-autocomplete"
                          />
                        </div>
                      ) : (
                        <Select value={selectedEmployeeId || 'all'} onValueChange={(value) => setSelectedEmployeeId(value === 'all' ? null : value)}>
                          <SelectTrigger className="form-select bg-brand-dark-lighter border-brand-gray/10 text-white hover:border-brand-primary transition-colors duration-200" data-testid="employee-select">
                            <SelectValue placeholder="Todos os funcionários" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos os Funcionários</SelectItem>
                            {realWorkforceEmployees.map((emp) => (
                              <SelectItem key={emp.employeeId} value={emp.employeeId}>
                                {emp.personalInfo.name} - {emp.currentEngagement?.roleName || 'N/A'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {loading && !evaluations.length && (
                <div className="flex flex-col items-center justify-center p-12">
                  <Loader2 className="w-12 h-12 text-brand-primary animate-spin mb-4" />
                  <span className="text-brand-gray">Carregando avaliações...</span>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="card border-error/30 p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-display font-medium text-white mb-1">Erro ao Carregar Avaliações</h4>
                      <p className="text-sm text-brand-gray mb-4">{error?.message || 'Erro desconhecido'}</p>
                      <button onClick={() => window.location.reload()} className="btn-secondary">
                        Tentar Novamente
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Primary Empty State (no evaluations at all) */}
              {!loading && !error && evaluations.length === 0 && statusFilter === 'all' && selectedEmployeeId === null && (
                <div className="flex flex-col items-center justify-center p-12 max-w-2xl mx-auto">
                  <div className="w-20 h-20 bg-brand-primary/10 rounded-monumenta flex items-center justify-center mb-6">
                    <FileText className="h-16 w-16 text-brand-primary" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white mb-3">Crie sua primeira avaliação</h3>
                  <p className="text-brand-gray text-center mb-6 leading-relaxed">
                    As avaliações de desempenho ajudam a identificar pontos fortes e oportunidades de desenvolvimento.
                    Avalie competências em 9 temas essenciais: técnica, liderança, comunicação, projetos e mais.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <Link
                      to="/performance/avaliacoes/form"
                      className="btn-primary flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Criar Nova Avaliação
                    </Link>
                    <Link
                      to="/performance/guia"
                      className="text-brand-blue hover:text-brand-primary transition-colors duration-200 flex items-center gap-2"
                    >
                      <BookOpen className="w-4 h-4" />
                      Ver guia de preenchimento
                    </Link>
                  </div>
                </div>
              )}

              {/* Filtered Empty State (filter returns no results) */}
              {!loading && !error && evaluations.length === 0 && (statusFilter !== 'all' || selectedEmployeeId !== null) && (
                <div className="flex flex-col items-center justify-center p-12">
                  <div className="w-20 h-20 bg-brand-gray/10 rounded-monumenta flex items-center justify-center mb-6">
                    <Filter className="h-12 w-12 text-brand-gray/50" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white mb-3">Nenhuma avaliação com este status</h3>
                  <p className="text-brand-gray text-center mb-6">
                    Não há avaliações '{getStatusLabel(statusFilter as any)}' no momento. Tente selecionar outro filtro ou criar uma nova avaliação.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setSelectedEmployeeId(null);
                      }}
                      className="btn-secondary"
                    >
                      Limpar Filtro
                    </button>
                    <Link
                      to="/performance/avaliacoes/form"
                      className="btn-primary flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Criar Avaliação
                    </Link>
                  </div>
                </div>
              )}

              {/* Evaluations List */}
              {!loading && !error && evaluations.length > 0 && (() => {
                // Pre-compute selected employee for manager view (outside the map)
                const selectedWorkforceEmployee = selectedEmployeeId
                  ? realWorkforceEmployees.find(emp => emp.employeeId === selectedEmployeeId)
                  : null
                const isMyEvaluationsView = !showManagerView
                const isManagerViewWithSelection = showManagerView && selectedEmployeeId

                return (
                <div className="space-y-4">
                  {evaluations.map((evaluation) => {
                    // Try to find employee in workforce list by evaluation's employeeId
                    const workforceEmployee = realWorkforceEmployees.find(emp => emp.employeeId === evaluation.employeeId)
                    const isCurrentUser = evaluation.employeeId === user?.employee?.employeeId

                    // Priority for name: 1) backend-provided employeeName, 2) workforce lookup, 3) current user, 4) fallback
                    const employee = workforceEmployee || (isManagerViewWithSelection ? selectedWorkforceEmployee : null) || (isCurrentUser || isMyEvaluationsView ? user?.employee : null)
                    const employeeName = evaluation.employeeName || employee?.personalInfo?.name || (isCurrentUser || isMyEvaluationsView ? user?.name : null) || `Funcionário ${evaluation.employeeId}`
                    const employeePictureUrl = employee?.personalInfo?.pictureUrl || (isCurrentUser || isMyEvaluationsView ? user?.avatar : undefined)
                    return (
                    <div
                      key={evaluation.evaluationId}
                      className="card-interactive p-4"
                      onClick={() => window.location.href = `/performance/avaliacoes/form?id=${evaluation.evaluationId}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {employeePictureUrl ? (
                          <img
                            src={employeePictureUrl}
                            alt={employeeName}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-black" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {/* Display employee name from workforce data (FR13 - PRD Week 50) */}
                            {employeeName}
                          </p>
                          <div className="flex items-center gap-4 mt-1 flex-wrap">
                            <p className="text-brand-gray text-sm">
                              Tipo: {evaluation.evaluatorType === 'self' ? 'Autoavaliação' :
                                    evaluation.evaluatorType === 'manager' ? 'Gestor' :
                                    evaluation.evaluatorType === 'hr' ? 'RH' : 'Par'}
                            </p>
                            {evaluation.evaluatorName && (
                              <p className="text-brand-gray text-sm">
                                Avaliador: <span className="text-white">{evaluation.evaluatorName}</span>
                              </p>
                            )}
                            <p className="text-brand-gray text-sm">
                              Período: {new Date(evaluation.evaluationPeriod.startDate).toLocaleDateString('pt-BR')} - {new Date(evaluation.evaluationPeriod.endDate).toLocaleDateString('pt-BR')}
                            </p>
                            <p className="text-brand-gray text-sm flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(evaluation.createdAt).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        {evaluation.overallScore && (
                          <div className="text-center">
                            <p className="text-lg font-display font-bold text-brand-primary">
                              {evaluation.overallScore.toFixed(1)}
                            </p>
                            <p className="text-brand-gray text-xs">Média</p>
                          </div>
                        )}
                        <Badge className={getStatusBadgeColor(evaluation.status)}>
                          {getStatusLabel(evaluation.status)}
                        </Badge>
                        {evaluation.status === 'draft' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Tem certeza que deseja enviar esta avaliação? Ela não poderá mais ser editada.')) {
                                handleSubmitEvaluation(evaluation.evaluationId);
                              }
                            }}
                            className="btn-primary btn-sm"
                          >
                            Enviar
                          </button>
                        )}
                        {(isAdmin || evaluation.status === 'draft') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteEvaluation(evaluation.evaluationId, employeeName, evaluation.status);
                            }}
                            className="p-2 text-brand-gray hover:text-error transition-colors duration-200 rounded-lg hover:bg-error/10"
                            title="Excluir avaliação"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )})}


                  {/* Pagination - TODO: Implement pagination with TanStack Query */}
                  {pagination.nextToken && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={() => console.log('Pagination not yet implemented with TanStack Query')}
                        disabled={loading}
                        className="btn-secondary"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Carregando...
                          </>
                        ) : (
                          'Carregar Mais Avaliações'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )})()}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: Analytics & Visualizations */}
      <div className="card bg-brand-dark-light border border-brand-gray/10 rounded-monumenta overflow-hidden">
        <button
          onClick={() => toggleSection('analytics')}
          className="w-full p-6 flex items-center justify-between hover:bg-brand-dark/30 transition-all duration-200 group"
        >
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-monumenta flex items-center justify-center transition-all duration-200 ${
              expandedSections.analytics ? 'bg-brand-primary' : 'bg-brand-dark'
            }`}>
              <BarChart3 className={`w-5 h-5 ${
                expandedSections.analytics ? 'text-black' : 'text-brand-primary'
              }`} />
            </div>
            <div className="text-left">
              <h2 className="font-display text-2xl font-bold text-white group-hover:text-brand-primary transition-colors duration-200">
                Análises & Visualizações
              </h2>
              <p className="text-sm text-brand-gray">
                Comparativos, distribuições e insights de performance
              </p>
            </div>
          </div>
          {expandedSections.analytics ? (
            <ChevronUp className="w-6 h-6 text-brand-primary" />
          ) : (
            <ChevronDown className="w-6 h-6 text-brand-gray group-hover:text-brand-primary transition-colors duration-200" />
          )}
        </button>

        <div
          className={`transition-all duration-300 ease-in-out ${
            expandedSections.analytics ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="border-t border-brand-gray/10">
            {!analyticsData ? (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-primary/10 rounded-monumenta mb-6">
                  <BarChart3 className="w-10 h-10 text-brand-primary" />
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-3">
                  Nenhuma Avaliação Concluída
                </h3>
                <p className="text-brand-gray mb-6 max-w-md mx-auto">
                  Complete suas primeiras avaliações para visualizar análises e insights de performance
                </p>
                <Link
                  to="/performance/avaliacoes/form"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Criar Primeira Avaliação
                </Link>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Comparative Evaluations Radar Chart */}
                <div className="card bg-brand-dark border border-brand-gray/10 rounded-monumenta overflow-hidden hover:border-brand-primary/30 transition-all duration-200">
                  <div className="p-6 border-b border-brand-gray/10">
                    <h3 className="font-display text-xl font-semibold text-white">
                      Comparativo de Avaliações - {currentEmployeeName}
                    </h3>
                    <p className="text-sm text-brand-gray mt-1">
                      Análise comparativa entre autoavaliação, gestor e RH
                    </p>
                  </div>
                  <div className="p-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <RadarChart data={analyticsData.radarData}>
                        <PolarGrid stroke={monumentaChartColors.grid} />
                        <PolarAngleAxis
                          dataKey="tema"
                          tick={{ fill: monumentaChartColors.text.secondary, fontSize: 11 }}
                        />
                        <PolarRadiusAxis
                          angle={0}
                          domain={[0, 5]}
                          tick={{ fill: monumentaChartColors.text.secondary, fontSize: 10 }}
                        />
                        <Radar
                          name="Autoavaliação"
                          dataKey="autoavaliacao"
                          {...monumentaChartPresets.radar.primary}
                        />
                        <Radar
                          name="Gestor"
                          dataKey="gestor"
                          {...monumentaChartPresets.radar.secondary}
                        />
                        <Radar
                          name="RH"
                          dataKey="rh"
                          {...monumentaChartPresets.radar.tertiary}
                        />
                        <Tooltip
                          contentStyle={monumentaChartTheme.tooltip.contentStyle}
                          labelStyle={monumentaChartTheme.tooltip.labelStyle}
                          itemStyle={monumentaChartTheme.tooltip.itemStyle}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Distribution Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Score Distribution */}
                  <div className="card-hover overflow-hidden">
                    <div className="p-6 border-b border-brand-gray/10">
                      <h3 className="font-display text-xl font-semibold text-white">Distribuição de Pontuações</h3>
                      <p className="text-sm text-brand-gray mt-1">
                        Distribuição das avaliações por faixa de desempenho
                      </p>
                    </div>
                    <div className="p-6">
                      {analyticsData.distributionData.some((d: any) => d.quantidade > 0) ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={analyticsData.distributionData.filter((d: any) => d.quantidade > 0)}
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              dataKey="quantidade"
                              label={({ nivel, percent }: any) => `${nivel.split(' ')[0]}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {analyticsData.distributionData.filter((d: any) => d.quantidade > 0).map((entry: any, index: number) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-[300px] text-brand-gray">
                          Dados insuficientes
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Average by Theme */}
                  <div className="card-hover overflow-hidden">
                    <div className="p-6 border-b border-brand-gray/10">
                      <h3 className="font-display text-xl font-semibold text-white">Média por Tema</h3>
                      <p className="text-sm text-brand-gray mt-1">
                        Pontuação média consolidada de todas as avaliações
                      </p>
                    </div>
                    <div className="p-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analyticsData.themeAverages}>
                          <CartesianGrid {...monumentaChartTheme.grid} />
                          <XAxis
                            dataKey="theme"
                            tick={{ fill: monumentaChartColors.text.secondary, fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis
                            tick={{ fill: monumentaChartColors.text.secondary, fontSize: 11 }}
                            domain={[0, 5]}
                          />
                          <Tooltip
                            contentStyle={monumentaChartTheme.tooltip.contentStyle}
                            labelStyle={monumentaChartTheme.tooltip.labelStyle}
                            itemStyle={monumentaChartTheme.tooltip.itemStyle}
                          />
                          <Bar
                            dataKey="avg"
                            fill={monumentaChartColors.primary}
                            radius={monumentaChartPresets.bar.radius}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Insights Card */}
                {analyticsData.insights && (
                  <div className="card border-brand-primary/20 p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-primary/20 rounded-monumenta flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-brand-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-display font-semibold text-white mb-2">Insights de Performance</h4>
                        <ul className="space-y-2 text-sm text-brand-gray">
                          <li className="flex items-start gap-2">
                            <span className="text-brand-primary">•</span>
                            <span>Maior pontuação média em <strong className="text-white">{analyticsData.insights.max.theme}</strong> ({analyticsData.insights.max.avg.toFixed(2)})</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-warning">•</span>
                            <span>Oportunidade de melhoria em <strong className="text-white">{analyticsData.insights.min.theme}</strong> ({analyticsData.insights.min.avg.toFixed(2)})</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-success">•</span>
                            <span>Total de <strong className="text-white">{evaluations.length}</strong> avaliações analisadas</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvaliacaoPerformance;
